/**
 * uptime.ts - API client and React Query hooks for Uptime (FOR-303).
 *
 * Admin → Uptime reads /api/admin/uptime/* (admin only). A site's own Uptime
 * page reads /api/site-uptime, scoped by the X-Active-License-Id header that
 * apiClient already sends. apiClient returns the response BODY, so `data` is
 * read straight off the result.
 *
 * Site query keys carry the license id: the query cache is persisted and must
 * never show one client's monitors on another's page.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { apiClient } from "@/lib/api"
import { useAuth } from "@/hooks/useAuth"
import type {
  MonitorDetail,
  MonitorInput,
  PulseData,
  PulseMinutes,
  RecentRange,
  SeriesRange,
  SiteUptimeMonitor,
  UptimeIncident,
  UptimeMonitor,
  UptimeOverview,
  UptimeProbe,
  UptimeSeries,
  UptimeWindow,
} from "@/lib/uptime"

export const uptimeApi = {
  overview: () => apiClient.get<{ data: UptimeOverview }>("/admin/uptime/overview").then((r) => r.data),
  monitor: (id: number, range: SeriesRange, recent: RecentRange = "24h") =>
    apiClient.get<{ data: MonitorDetail }>(`/admin/uptime/monitors/${id}`, { params: { range, recent } }).then((r) => r.data),
  pulse: (id: number, minutes: PulseMinutes) =>
    apiClient.get<{ data: PulseData }>(`/admin/uptime/monitors/${id}/pulse`, { params: { minutes } }).then((r) => r.data),
  create: (body: MonitorInput) => apiClient.post<{ data: UptimeMonitor }>("/admin/uptime/monitors", body).then((r) => r.data),
  update: (id: number, body: Partial<MonitorInput>) => apiClient.put<{ data: UptimeMonitor }>(`/admin/uptime/monitors/${id}`, body).then((r) => r.data),
  remove: (id: number) => apiClient.delete<{ data: { deleted: boolean } }>(`/admin/uptime/monitors/${id}`),
  checkNow: (id: number) => apiClient.post<{ data: { monitor: UptimeMonitor } }>(`/admin/uptime/monitors/${id}/check`).then((r) => r.data),
  rotateToken: (id: number) => apiClient.post<{ data: UptimeMonitor }>(`/admin/uptime/monitors/${id}/rotate-token`).then((r) => r.data),
  incidents: (status: "open" | "resolved" | "all", page = 1) =>
    apiClient.get<{ data: UptimeIncident[]; meta: { current_page: number; last_page: number; total: number } }>("/admin/uptime/incidents", {
      params: { status, page, per_page: 50 },
    }),
  acknowledge: (id: number) => apiClient.post<{ data: UptimeIncident }>(`/admin/uptime/incidents/${id}/acknowledge`).then((r) => r.data),
  saveNotes: (id: number, notes: string) => apiClient.put<{ data: UptimeIncident }>(`/admin/uptime/incidents/${id}`, { notes }).then((r) => r.data),
  probes: () => apiClient.get<{ data: UptimeProbe[] }>("/admin/uptime/probes").then((r) => r.data),
  maintenance: () => apiClient.get<{ data: UptimeWindow[] }>("/admin/uptime/maintenance").then((r) => r.data),
  startMaintenance: (body: { monitor_id: number | null; minutes: number; note?: string }) =>
    apiClient.post<{ data: UptimeWindow }>("/admin/uptime/maintenance", body).then((r) => r.data),
  endMaintenance: (id: number) => apiClient.delete<{ data: { ended: boolean } }>(`/admin/uptime/maintenance/${id}`),
  site: (recent: RecentRange = "24h") =>
    apiClient.get<{ data: { monitors: SiteUptimeMonitor[] } }>("/site-uptime", { params: { recent } }).then((r) => r.data),
  siteSeries: (id: number, range: SeriesRange) =>
    apiClient.get<{ data: UptimeSeries }>(`/site-uptime/${id}/series`, { params: { range } }).then((r) => r.data),
}

export const uptimeKeys = {
  all: ["uptime"] as const,
  overview: () => ["uptime", "overview"] as const,
  monitor: (id: number, range: SeriesRange, recent: RecentRange) => ["uptime", "monitor", id, range, recent] as const,
  pulse: (id: number, minutes: PulseMinutes) => ["uptime", "pulse", id, minutes] as const,
  incidents: (status: string, page: number) => ["uptime", "incidents", status, page] as const,
  probes: () => ["uptime", "probes"] as const,
  maintenance: () => ["uptime", "maintenance"] as const,
  site: (license: number, recent: RecentRange) => ["uptime", "site", license, recent] as const,
  siteSeries: (license: number, id: number, range: SeriesRange) => ["uptime", "site", license, "series", id, range] as const,
}

export function useUptimeOverview() {
  return useQuery({ queryKey: uptimeKeys.overview(), queryFn: uptimeApi.overview, refetchInterval: 60_000, meta: { persist: false } })
}

export function useUptimeMonitor(id: number, range: SeriesRange, recent: RecentRange = "24h") {
  return useQuery({
    queryKey: uptimeKeys.monitor(id, range, recent),
    queryFn: () => uptimeApi.monitor(id, range, recent),
    enabled: id > 0,
    refetchInterval: 60_000,
    placeholderData: (prev) => prev,
    meta: { persist: false },
  })
}

/** Every check in the last N minutes, for the pulse trace. Refreshed every 30 s so new beats arrive. */
export function usePulse(id: number, minutes: PulseMinutes) {
  return useQuery({
    queryKey: uptimeKeys.pulse(id, minutes),
    queryFn: () => uptimeApi.pulse(id, minutes),
    enabled: id > 0,
    refetchInterval: 30_000,
    placeholderData: (prev) => prev,
    meta: { persist: false },
  })
}

export function useUptimeIncidents(status: "open" | "resolved" | "all", page: number) {
  return useQuery({ queryKey: uptimeKeys.incidents(status, page), queryFn: () => uptimeApi.incidents(status, page), meta: { persist: false } })
}

export function useUptimeProbes() {
  return useQuery({ queryKey: uptimeKeys.probes(), queryFn: uptimeApi.probes, refetchInterval: 60_000, meta: { persist: false } })
}

export function useUptimeMaintenance() {
  return useQuery({ queryKey: uptimeKeys.maintenance(), queryFn: uptimeApi.maintenance, meta: { persist: false } })
}

/** Invalidate everything Uptime after a write. */
export function useUptimeInvalidate() {
  const qc = useQueryClient()
  return () => qc.invalidateQueries({ queryKey: uptimeKeys.all })
}

export function useUptimeMutation<TArgs, TResult>(fn: (args: TArgs) => Promise<TResult>) {
  const invalidate = useUptimeInvalidate()
  return useMutation({ mutationFn: fn, onSuccess: () => invalidate() })
}

/** The current site's linked monitors. Empty for a site Uptime does not watch. */
export function useSiteUptime(recent: RecentRange = "24h") {
  const { currentLicense } = useAuth()
  const license = Number(currentLicense?.id ?? 0)
  return useQuery({
    queryKey: uptimeKeys.site(license, recent),
    queryFn: () => uptimeApi.site(recent),
    placeholderData: (prev) => prev,
    enabled: license > 0,
    refetchInterval: 120_000,
    staleTime: 60_000,
  })
}

export function useSiteUptimeSeries(id: number, range: SeriesRange) {
  const { currentLicense } = useAuth()
  const license = Number(currentLicense?.id ?? 0)
  return useQuery({
    queryKey: uptimeKeys.siteSeries(license, id, range),
    queryFn: () => uptimeApi.siteSeries(id, range),
    enabled: license > 0 && id > 0,
    placeholderData: (prev) => prev,
    meta: { persist: false },
  })
}
